import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { getLogger } from '@confluence/logger';

const logger = getLogger('EditorControlsBannerDismissal');

export type BannerType = 'toolbar-top' | 'toolbar-inline';
type BannerDismissal = {
	count?: number;
	firstViewed?: number; // Timestamp when the banner was first viewed
};

type LocalStorageItem = {
	[key in BannerType]?: BannerDismissal;
};

const NEW_PINNING_STORAGE_KEY =
	PERSISTED_KEYS_ON_SERVER.PERSISTED_EDITOR_CONTROLS_ONBOARDING_BANNER_NEW_PINNING_DISMISSED;

const AUTO_DISMISSAL_THRESHOLD = 1000 * 60 * 60 * 24 * 7; // 7 days in milliseconds

export const getBannerType = (isTopBanner: boolean): BannerType => {
	return isTopBanner ? 'toolbar-top' : 'toolbar-inline';
};

const getLocalStorageItem = (storageKey: string): LocalStorageItem | undefined => {
	try {
		const itemString = localStorage.getItem(storageKey);
		if (!itemString) {
			return undefined;
		}
		const item = JSON.parse(itemString);
		if (
			typeof item === 'object' &&
			item !== null &&
			!Array.isArray(item) &&
			Object.keys(item).every((key) => ['toolbar-top', 'toolbar-inline'].includes(key))
		) {
			return item;
		}
	} catch (error) {
		logger.error`Error parsing local storage key(${storageKey}): ${error}`;
	}
	return undefined;
};

const recordFirstViewTime = (bannerType: BannerType, item: LocalStorageItem | undefined) => {
	const firstViewed = { firstViewed: Date.now() };
	try {
		if (!item) {
			localStorage.setItem(NEW_PINNING_STORAGE_KEY, JSON.stringify({ [bannerType]: firstViewed }));
		} else {
			item[bannerType] = { ...item[bannerType], ...firstViewed };
			localStorage.setItem(NEW_PINNING_STORAGE_KEY, JSON.stringify(item));
		}
	} catch (error) {
		logger.error`Error setting local storage key(${NEW_PINNING_STORAGE_KEY}): ${error}`;
	}
};

export const isBannerDismissedInLocalStorage = (bannerType: BannerType): boolean => {
	const localStorageItem = getLocalStorageItem(NEW_PINNING_STORAGE_KEY);
	const { firstViewed, count: dismissalCount } = localStorageItem?.[bannerType] || {};

	// Record first view time if it does not exist. Do this before returning to
	// ensure we record a time even if there are no prior records
	if (firstViewed === undefined) {
		recordFirstViewTime(bannerType, localStorageItem);
		// Note that we do not early return here. Banner could already be dismissed
		// prior to "firstViewed" for users who dismissed before this change.
	}

	// No local storage = no banner dismissal
	if (!localStorageItem) {
		return false;
	}

	// If banner was first viewed more than a week ago, hide it even if it had not been dismissed
	const shouldAutoDismiss = firstViewed && Date.now() - firstViewed > AUTO_DISMISSAL_THRESHOLD;
	if (shouldAutoDismiss) {
		return true;
	}

	// If count is anything other than undefined, it means the banner has been dismissed (including if count is 0!)
	return dismissalCount !== undefined;
};

export const persistBannerDismissalNewPinning = (bannerType: BannerType): void => {
	let item = getLocalStorageItem(NEW_PINNING_STORAGE_KEY);
	if (!item) {
		item = { [bannerType]: { count: 0 } };
	} else {
		const existingDismissal = item[bannerType];
		item[bannerType] = {
			...existingDismissal,
			count: 0,
		};
	}
	try {
		localStorage.setItem(NEW_PINNING_STORAGE_KEY, JSON.stringify(item));
	} catch (error) {
		logger.error`Error setting local storage key(${NEW_PINNING_STORAGE_KEY}): ${error}`;
	}
};
